import { OrderPaymentChoice } from '../../types/admin_portal/orders/table/OrderPaymentChoice.js';
import { OrdersTableTab } from '../../types/admin_portal/orders/table/OrdersTableTab.js';
import { VehicleExternalStatus, VehiclePaymentSelection } from '../../types/purchasedVehicles/PurchasedVehicle.js';

/**
 * Formats orders table tab for display
 * Converts orders table tab enum values to human-readable format for UI display
 */
function labelFromOrdersTableTab(tab) {
    switch (tab) {
        case OrdersTableTab.ALL:
            return 'All';
        case OrdersTableTab.PENDING:
            return 'Pending';
        case OrdersTableTab.SALES_PROCESSING:
            return 'Sales processing';
        case OrdersTableTab.KICK_OFF_FULFILLMENT:
            return 'Kick off fulfillment';
        case OrdersTableTab.IN_FULFILLMENT:
            return 'In fulfillment';
        case OrdersTableTab.FULFILLED:
            return 'Fulfilled';
        case OrdersTableTab.CANCELED:
            return 'Canceled';
        default:
            return '-';
    }
}
/**
 * Map from VehicleExternalStatus to OrdersTableTab.
 * This is the single source of truth for the mapping relationship.
 * The reverse mapping (OrdersTableTab -> VehicleExternalStatus[]) is computed on-demand.
 */
const EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP = new Map([
    [VehicleExternalStatus.PENDING, OrdersTableTab.PENDING],
    [VehicleExternalStatus.AWAITING_OEM_ACTION, OrdersTableTab.SALES_PROCESSING],
    [VehicleExternalStatus.AWAITING_BUYER_OR_EKHO_ACTION, OrdersTableTab.SALES_PROCESSING],
    [VehicleExternalStatus.KICK_OFF_FULFILLMENT, OrdersTableTab.KICK_OFF_FULFILLMENT],
    [VehicleExternalStatus.FULFILLMENT_IN_PROGRESS, OrdersTableTab.IN_FULFILLMENT],
    [VehicleExternalStatus.FULFILLED, OrdersTableTab.FULFILLED],
    [VehicleExternalStatus.CANCELED, OrdersTableTab.CANCELED],
]);
/**
 * Converts VehicleExternalStatus enum values to OrdersTableTab enum values.
 * @param externalStatus - VehicleExternalStatus enum value
 * @returns OrdersTableTab enum value, or undefined if no mapping found
 */
function ordersTableTabFromExternalStatus(externalStatus) {
    return EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP.get(externalStatus);
}
/**
 * Converts OrdersTableTab enum value to array of VehicleExternalStatus enum values.
 * Computes the reverse mapping from EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP on-demand.
 * @param tab - OrdersTableTab enum value
 * @returns Array of VehicleExternalStatus enum values, or empty array if no mapping found
 */
function externalStatusesFromOrdersTableTab(tab) {
    if (tab === OrdersTableTab.ALL) {
        // Return all VehicleExternalStatus values present in the mapping
        return Array.from(EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP.keys());
    }
    const reverseMap = new Map();
    for (const [externalStatus, tab] of EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP) {
        const existing = reverseMap.get(tab) || [];
        reverseMap.set(tab, [...existing, externalStatus]);
    }
    return reverseMap.get(tab) || [];
}
/**
 * Converts array of OrdersTableTab enum values to array of VehicleExternalStatus enum values.
 * Computes the reverse mapping from EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP on-demand.
 * @param tabs - Array of OrdersTableTab enum values
 * @returns Array of unique VehicleExternalStatus enum values, or empty array if no mappings found
 */
function externalStatusesFromOrdersTableTabs(tabs) {
    if (!tabs)
        return [];
    // If any tab is ALL, return all statuses
    if (tabs.includes(OrdersTableTab.ALL)) {
        return Array.from(EXTERNAL_STATUS_TO_ORDERS_TABLE_TAB_MAP.keys());
    }
    const externalStatuses = tabs.flatMap((tab) => externalStatusesFromOrdersTableTab(tab));
    return Array.from(new Set(externalStatuses));
}
/**
 * Formats payment choice for display
 * Converts payment choice enum values to human-readable format for UI display
 */
function labelFromPaymentChoice(paymentChoice) {
    switch (paymentChoice) {
        case OrderPaymentChoice.FINANCING:
            return 'Financing';
        case OrderPaymentChoice.FULL_CASH:
            return 'Full cash';
        default:
            return undefined;
    }
}
/**
 * Map from VehiclePaymentSelection to OrderPaymentChoice.
 * This is the single source of truth for the mapping relationship.
 * The reverse mapping (OrderPaymentChoice -> VehiclePaymentSelection[]) is computed on-demand.
 */
const PAYMENT_SELECTION_TO_PAYMENT_CHOICE_MAP = new Map([
    [VehiclePaymentSelection.NATIVE_FINANCING, OrderPaymentChoice.FINANCING],
    [VehiclePaymentSelection.OTHER_FINANCING, OrderPaymentChoice.FINANCING],
    [VehiclePaymentSelection.KLARNA_CHECKOUT_FULL_PAYMENT, OrderPaymentChoice.FINANCING],
    [VehiclePaymentSelection.CRYPTO_CHECKOUT_FULL_PAYMENT, OrderPaymentChoice.FULL_CASH],
    [VehiclePaymentSelection.CHECKOUT_FULL_PAYMENT, OrderPaymentChoice.FULL_CASH],
    [VehiclePaymentSelection.CASH_EARLY, OrderPaymentChoice.FULL_CASH],
    [VehiclePaymentSelection.CASH, OrderPaymentChoice.FULL_CASH],
]);
/**
 * Converts VehiclePaymentSelection enum values to OrderPaymentChoice enum values.
 * @param paymentSelection - VehiclePaymentSelection enum value
 * @returns OrderPaymentChoice enum value, or undefined if no mapping found
 */
function paymentChoiceFromPaymentSelection(paymentSelection) {
    return PAYMENT_SELECTION_TO_PAYMENT_CHOICE_MAP.get(paymentSelection);
}
/**
 * Converts array of OrderPaymentChoice enum values to array of VehiclePaymentSelection enum values.
 * Computes the reverse mapping from PAYMENT_SELECTION_TO_PAYMENT_CHOICE_MAP on-demand.
 * @param paymentChoice - Array of OrderPaymentChoice enum values
 * @returns Array of unique VehiclePaymentSelection enum values, or empty array if no mappings found
 */
function paymentSelectionsFromPaymentChoices(paymentChoice) {
    if (!paymentChoice)
        return [];
    const reverseMap = new Map();
    for (const [paymentSelection, orderPaymentChoice] of PAYMENT_SELECTION_TO_PAYMENT_CHOICE_MAP) {
        const existing = reverseMap.get(orderPaymentChoice) || [];
        reverseMap.set(orderPaymentChoice, [...existing, paymentSelection]);
    }
    const selections = paymentChoice
        .map((choice) => reverseMap.get(choice))
        .filter((mapped) => mapped !== undefined)
        .flatMap((mapped) => mapped);
    return selections.length > 0 ? Array.from(new Set(selections)) : [];
}

export { externalStatusesFromOrdersTableTab, externalStatusesFromOrdersTableTabs, labelFromOrdersTableTab, labelFromPaymentChoice, ordersTableTabFromExternalStatus, paymentChoiceFromPaymentSelection, paymentSelectionsFromPaymentChoices };
//# sourceMappingURL=mapping.js.map
